import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ModoLocutor } from "../imagens/ModoLocutor";
import { ModoGravarVoz } from "../imagens/ModoGravarVoz";
import { useAudioRecorder } from "../hooks/useAudioRecorder";

function Locucao({ user, produtos, onLocucaoGerada, video, setVisivel, creditos, playRef, setAudioGravadoUrl, setModoGravando, modoGravando }) {
  const [gerando, setGerando] = useState(false);
  const [statusBotao, setStatusBotao] = useState("Gerar Locucao");
  const [progressIndex, setProgressIndex] = useState(-1);
  // Inicia no modo "locutor" com voiceId vazio = voz "Padrão" (gratuita) selecionada
  const [voz, setVoz] = useState("locutor");
  const [voiceId, setVoiceId] = useState("");

  const [allVoices, setAllVoices] = useState([]);
  const [previewPadrao, setPreviewPadrao] = useState("");

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Modo Gravar Voz
  const { isRecording, audioUrl: audioGravadoUrl, startRecording, stopRecording, resetRecording } = useAudioRecorder();
  const [gravacaoStep, setGravacaoStep] = useState("idle");
  const [countdownValue, setCountdownValue] = useState(3);
  const [vozPadraoCache, setVozPadraoCache] = useState(null);
  // Ref para capturar o audioUrl assíncrono do onstop do MediaRecorder
  const aguardandoAudioRef = useRef(false);
  const paradaSolicitadaRef = useRef(false);
  const ultimoFrameRef = useRef(null);

  useEffect(() => {
    fetch("https://videooferta.com/api/vozes.php")
      .then((r) => r.json())
      .then((data) => {
        if (data.success && data.vozes) {
          setAllVoices(data.vozes);
          setPreviewPadrao(data.preview_padrao || "");
        }
      })
      .catch((err) => console.error("Erro ao carregar vozes:", err));
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [dropdownOpen]);

  const nomeEstab = (video?.usuario_nome_estabelecimento || "").trim();

  const normalizarParaComparar = (s) =>
    String(s || "")
      .toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ")
      .replace(/[.,!]+$/g, "")
      .trim();

  const contemEstabelecimentoNaLocucao = (textoLocucao, nomeEstabelecimento) => {
    if (!nomeEstabelecimento) return true;
    const alvo = normalizarParaComparar(nomeEstabelecimento);
    if (!alvo) return true;
    const base = normalizarParaComparar(textoLocucao);
    return base.includes(alvo);
  };

  const adicionarEstabelecimentoNaLocucao = (textoLocucao, nomeEstabelecimento) => {
    const base = String(textoLocucao || "").trim();
    if (!nomeEstabelecimento) return base;
    if (contemEstabelecimentoNaLocucao(base, nomeEstabelecimento)) return base;
    if (!base) return `${nomeEstabelecimento}.`;
    const sep = /[.!?]$/.test(base) ? " " : ". ";
    return `${base}${sep}${nomeEstabelecimento}.`;
  };

  const textoIntroBase = (video?.texto_audio || "").trim();
  const textoIntroInicial = adicionarEstabelecimentoNaLocucao(textoIntroBase, nomeEstab);

  const fechamentoPadrao = nomeEstab
    ? `Essas e outras ofertas voce so encontra aqui. ${nomeEstab}!`
    : "Essas e outras ofertas voce so encontra aqui.";
  const [textoOutro, setTextoOutro] = useState(fechamentoPadrao);

  const [tocando, setTocando] = useState(false);
  const audioRef = useRef(null);

  const urlParams = new URLSearchParams(window.location.search);
  const currentId = urlParams.get("id");

  // Só bloqueia quando uma voz paga (voiceId real) está selecionada e não há créditos.
  // "Padrão" (voiceId vazio) é sempre gratuito.
  const locutorBloqueado = voz === "locutor" && voiceId !== "" && Number(creditos) <= 0;

  const selectedVoice = allVoices.find((v) => v.id === voiceId) || null;

  const previewUrl =
    voz === "padrao" || (voz === "locutor" && voiceId === "")
      ? previewPadrao
      : selectedVoice?.preview || "";

  async function gerarAudioProduto(produtoId, texto, forcedVoz) {
    const vozEfetiva = forcedVoz || voz;
    // "Padrão" (locutor sem voiceId) é gratuito → envia voz "padrao" para o backend não descontar crédito
    const vozParaEnviar = vozEfetiva === "locutor" && !voiceId ? "padrao" : vozEfetiva;
    const payload = {
      produto_id: produtoId,
      texto,
      id_video: currentId,
      voz: vozParaEnviar,
    };
    if (vozParaEnviar === "locutor" && voiceId) {
      payload.voice_id = voiceId;
    }
    const resp = await fetch("https://videooferta.com/api/geraraudio.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    return await resp.json();
  }

  async function gerarLocucao(silent = false, forcedVoz = null) {
    if (gerando) return;

    setGerando(true);
    setProgressIndex(0);
    if (!silent) setStatusBotao("Gerando introducao...");

    try {
      const introText = document.querySelector(".locu-textarea-intro")?.value || textoIntroInicial;
      const textareas = document.querySelectorAll(".locu-textarea-produto");
      const outroText = (textoOutro || "").trim();

      await gerarAudioProduto("intro", introText, forcedVoz);

      for (let i = 0; i < produtos.length; i++) {
        setProgressIndex(i + 1);
        if (!silent) setStatusBotao(`Gerando audio ${i + 1} de ${produtos.length}...`);
        const produto = produtos[i];
        const texto = textareas[i]?.value || produto.texto_audio || produto.titulo;
        await gerarAudioProduto(produto.id, texto, forcedVoz);
      }

      if (outroText !== "") {
        setProgressIndex(produtos.length + 1);
        if (!silent) setStatusBotao("Gerando fechamento...");
        await gerarAudioProduto("outro", outroText, forcedVoz);
      }

      if (!silent) {
        setStatusBotao("Finalizando...");
        await onLocucaoGerada();
        setStatusBotao("Gerar Locucao");
      }
    } catch (err) {
      console.error(err);
      if (!silent) setStatusBotao("Erro ao gerar");
    } finally {
      setGerando(false);
      setProgressIndex(-1);
    }
  }

  // Hash simples dos produtos para cache
  const getProdutosHash = () => {
    if (!produtos || produtos.length === 0) return "";
    return produtos.map((p) => p.id).join("-");
  };

  const iniciarCapturaComPreview = useCallback(() => {
    setGravacaoStep("recording");
    paradaSolicitadaRef.current = false;
    ultimoFrameRef.current = null;

    startRecording()
      .then(() => {
        setModoGravando(true);
        playRef.current?.seekTo?.(0);
        setTimeout(() => {
          playRef.current?.play?.();
        }, 50);
      })
      .catch((err) => {
        console.error("Falha ao iniciar gravacao:", err);
        setGravacaoStep("idle");
      });
  }, [startRecording, setModoGravando, playRef]);

  // Se trocar voz (padrao/locutor) → desativa modo gravando e limpa tudo
  useEffect(() => {
    if (voz !== "gravar") {
      setModoGravando(false);
      setAudioGravadoUrl(null);
      resetRecording();
      setGravacaoStep("idle");
      aguardandoAudioRef.current = false;
      paradaSolicitadaRef.current = false;
      ultimoFrameRef.current = null;
    }
  }, [voz, setModoGravando, setAudioGravadoUrl, resetRecording]);

  // Ao desmontar Locucao → limpa apenas refs internas (preserva áudio gravado)
  useEffect(() => {
    return () => {
      aguardandoAudioRef.current = false;
      paradaSolicitadaRef.current = false;
      ultimoFrameRef.current = null;
    };
  }, []);

  // Mobile: parar gravação quando modoGravando é setado false externamente
  useEffect(() => {
    if (!modoGravando && gravacaoStep === "recording" && isRecording) {
      pararGravacao();
    }
  }, [modoGravando]);

  // Iniciar fluxo de gravacao
  const iniciarFluxoGravarVoz = async () => {
    const hashAtual = getProdutosHash();

    // Passo 1: Gerar vozes padrao se nao tiver cache ou produtos mudaram
    if (!vozPadraoCache || vozPadraoCache.hash !== hashAtual) {
      setStatusBotao("Preparando sincronizacao...");
      await gerarLocucao(true, "padrao");
      setVozPadraoCache({ hash: hashAtual, gerado: true });
    }

    // Passo 2: Contagem regressiva
    setGravacaoStep("countdown");
    setCountdownValue(3);

    let count = 3;
    const timer = setInterval(() => {
      count -= 1;
      setCountdownValue(count);
      if (count <= 0) {
        clearInterval(timer);
        setCountdownValue(0);
        // Deixa o "Gravando!" visível por um instante antes de iniciar.
        setTimeout(() => {
          iniciarCapturaComPreview();
        }, 300);
      }
    }, 1000);
  };

  // Parar gravacao — nao usa audioGravadoUrl no closure (ainda e null aqui)
  // O useEffect abaixo observa audioGravadoUrl e finaliza quando ficar disponivel
  const pararGravacao = useCallback(() => {
    if (paradaSolicitadaRef.current) return;
    paradaSolicitadaRef.current = true;
    aguardandoAudioRef.current = true;
    stopRecording();
    setGravacaoStep("loading_final");
    playRef.current?.pause?.();
  }, [stopRecording, playRef]);

  // Reage quando o MediaRecorder.onstop preencher audioGravadoUrl
  useEffect(() => {
    if (!aguardandoAudioRef.current || !audioGravadoUrl) return;
    aguardandoAudioRef.current = false;
    paradaSolicitadaRef.current = false;
    ultimoFrameRef.current = null;

    // Publica o audio no player (re-monta a composition com o blob)
    setAudioGravadoUrl(audioGravadoUrl);

    setTimeout(() => {
      setGravacaoStep("finished");
      playRef.current?.seekTo?.(0);
      setTimeout(() => {
        playRef.current?.play?.();
      }, 50);
    }, 500);
  }, [audioGravadoUrl, setAudioGravadoUrl, playRef]);

  // Usa evento de fim do Remotion Player para parar no final com precisão.
  // Mantém o polling abaixo como fallback caso o player ainda não esteja pronto.
  useEffect(() => {
    if (gravacaoStep !== "recording" || !isRecording) return;
    const api = playRef.current;
    if (!api?.addEventListener) return;

    const onEnded = () => {
      pararGravacao();
    };

    api.addEventListener("ended", onEnded);
    return () => {
      api.removeEventListener?.("ended", onEnded);
    };
  }, [gravacaoStep, isRecording, pararGravacao, playRef]);

  // Monitora fim da gravacao: para quando o video terminar (usando duracao real do player)
  useEffect(() => {
    if (gravacaoStep !== "recording" || !isRecording) return;

    const checkInterval = setInterval(() => {
      const api = playRef.current;
      if (!api) return;
      const frame = api.getCurrentFrame?.();
      const totalFrames = api.getDuration?.();
      if (frame == null || totalFrames == null) return;

      // Fallback: se o player rebobinar para o início entre dois checks,
      // assume que alcançou o fim e dispara a parada automática.
      if (
        typeof ultimoFrameRef.current === "number" &&
        ultimoFrameRef.current >= totalFrames - 5 &&
        frame < ultimoFrameRef.current
      ) {
        pararGravacao();
        return;
      }

      ultimoFrameRef.current = frame;

      if (frame >= totalFrames - 1) {
        pararGravacao();
      }
    }, 200);

    return () => clearInterval(checkInterval);
  }, [gravacaoStep, isRecording, pararGravacao, playRef]);

  // Botao principal
  const handleBotaoPrincipal = () => {
    if (voz === "gravar") {
      if (gravacaoStep === "idle") {
        iniciarFluxoGravarVoz();
      } else if (gravacaoStep === "recording") {
        pararGravacao();
      } else if (gravacaoStep === "finished") {
        // "Gravar outra vez" — volta para countdown sem regerar vozes
        // Limpa gravacao anterior e reinicia
        resetRecording();
        setAudioGravadoUrl(null);
        setGravacaoStep("countdown");
        setCountdownValue(3);
        let count = 3;
        const timer = setInterval(() => {
          count -= 1;
          setCountdownValue(count);
          if (count <= 0) {
            clearInterval(timer);
            setCountdownValue(0);
            setTimeout(() => {
              iniciarCapturaComPreview();
            }, 300);
          }
        }, 1000);
      }
    } else {
      if (locutorBloqueado) {
        alert("Voce precisa de creditos para usar a voz de locutor.");
        return;
      }
      gerarLocucao(false);
    }
  };

  function togglePlay() {
    const audio = audioRef.current;
    if (!audio || !previewUrl) return;
    if (tocando) audio.pause();
    else {
      audio.currentTime = 0;
      audio.play();
    }
  }

  const totalSteps = (produtos?.length || 0) + 1;
  const progress = gerando && totalSteps > 0 ? ((progressIndex + 1) / totalSteps) * 100 : 0;

  return (
    <div className="col2 prod-panel">
      {/* Overlay para gravacao */}
      <AnimatePresence>
        {gravacaoStep === "countdown" && (
          <motion.div
            className="locu-overlay locu-overlay--countdown"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.span
              key={countdownValue}
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 1.5, opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="locu-countdown"
            >
              {countdownValue > 0 ? countdownValue : "Gravando!"}
            </motion.span>
          </motion.div>
        )}

        {gravacaoStep === "loading_final" && (
          <motion.div
            className="locu-overlay locu-overlay--loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <span className="prod-spinner-sm" style={{ width: 40, height: 40, borderWidth: 4 }} />
            <p className="locu-loading-text">Carregando versao final, um momento</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <motion.div className="prod-header" initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <div className="prod-header__top">
          <div className="prod-header__title-group">
            <div className="prod-header__icon-wrap">
              <i className="fa-solid fa-microphone-lines" />
            </div>
            <div>
              <h2 className="prod-header__title">Locucao</h2>
              <p className="prod-header__subtitle">Gere audios para seu video</p>
            </div>
          </div>
          <button className="prod-header__close" onClick={() => setVisivel("")} aria-label="Fechar">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
      </motion.div>

      {/* Body */}
      <motion.div className="prod-body" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.35 }}>
        {/* Seletor de tipo de voz */}
        <div className="locu-section">
          <label className="edit-label">Tipo de voz</label>
          <span className="locu-section__hint">Escolha como a voz sera gerada.</span>
          <div className="locu-voice-grid">
            <button
              className={`locu-voice-card${voz === "locutor" ? " locu-voice-card--active" : ""}`}
              onClick={() => {
                setVoz("locutor");
                setTocando(false);
                if (audioRef.current) audioRef.current.pause();
              }}
            >
              <div className="locu-voice-card__icon" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ModoLocutor style={{ width: 28, height: 28, color: "#73207C" }} />
              </div>
              <span className="locu-voice-card__label">Locutor</span>
              <span className="locu-voice-card__badge locu-voice-card__badge--pago">5</span>
              {voz === "locutor" && (
                <motion.div className="locu-voice-card__check" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 400, damping: 20 }}>
                  <i className="fa-solid fa-check" />
                </motion.div>
              )}
            </button>

            <button
              className={`locu-voice-card${voz === "gravar" ? " locu-voice-card--active" : ""}`}
              onClick={() => {
                setVoz("gravar");
                setVoiceId("");
                setTocando(false);
                if (audioRef.current) audioRef.current.pause();
              }}
            >
              <div className="locu-voice-card__icon" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ModoGravarVoz style={{ width: 24, height: 24, color: "#73207C" }} />
              </div>
              <span className="locu-voice-card__label">Gravar Voz</span>
              <span className="locu-voice-card__badge locu-voice-card__badge--gratis">Gratis</span>
              {voz === "gravar" && (
                <motion.div className="locu-voice-card__check" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 400, damping: 20 }}>
                  <i className="fa-solid fa-check" />
                </motion.div>
              )}
            </button>
          </div>

          {locutorBloqueado && (
            <motion.div className="locu-warning" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
              <i className="fa-solid fa-triangle-exclamation" />
              Voce precisa de creditos para usar a voz de locutor.
            </motion.div>
          )}
        </div>

        {/* Seletor de voz especifica (apenas quando locutor esta ativo) */}
        <AnimatePresence>
          {voz === "locutor" && (
            <motion.div className="locu-section" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25 }}>
              <div className="locu-field-header">
                <i className="fa-solid fa-users locu-field-header__icon" />
                <label className="edit-label">Escolha a voz</label>
              </div>
              <span className="locu-section__hint">Selecione a voz ideal para o seu conteudo.</span>

              <div className="locu-dropdown" ref={dropdownRef}>
                <button className={`locu-dropdown__trigger${dropdownOpen ? " locu-dropdown__trigger--open" : ""}`} onClick={() => setDropdownOpen(!dropdownOpen)} type="button">
                  <span className="locu-dropdown__trigger-avatar">{selectedVoice ? selectedVoice.emoji : "🎙️"}</span>
                  <span className="locu-dropdown__trigger-text">
                    {selectedVoice ? <><strong>{selectedVoice.label}</strong> — {selectedVoice.desc}</> : <><strong>Padrão</strong> — Voz padrão do sistema</>}
                  </span>
                  <i className={`fa-solid fa-chevron-down locu-dropdown__chevron${dropdownOpen ? " locu-dropdown__chevron--open" : ""}`} />
                </button>

                <AnimatePresence>
                  {dropdownOpen && (
                    <motion.ul className="locu-dropdown__list" initial={{ opacity: 0, y: -6, scaleY: 0.96 }} animate={{ opacity: 1, y: 0, scaleY: 1 }} exit={{ opacity: 0, y: -6, scaleY: 0.96 }} transition={{ duration: 0.18 }}>
                      {/* Opção Padrão */}
                      <li className={`locu-dropdown__item${voiceId === "" ? " locu-dropdown__item--active" : ""}`} onClick={() => { setVoiceId(""); setTocando(false); if (audioRef.current) audioRef.current.pause(); setDropdownOpen(false); }}>
                        <span className="locu-dropdown__item-avatar">🎙️</span>
                        <span className="locu-dropdown__item-text"><strong>Padrão</strong> — Voz padrão do sistema</span>
                        {voiceId === "" && (
                          <motion.span className="locu-dropdown__item-check" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 500, damping: 25 }}>
                            <i className="fa-solid fa-check" />
                          </motion.span>
                        )}
                      </li>
                      {allVoices.map((v) => (
                        <li key={v.id} className={`locu-dropdown__item${voiceId === v.id ? " locu-dropdown__item--active" : ""}`} onClick={() => { setVoiceId(v.id); setTocando(false); if (audioRef.current) audioRef.current.pause(); setDropdownOpen(false); }}>
                          <span className="locu-dropdown__item-avatar">{v.emoji}</span>
                          <span className="locu-dropdown__item-text"><strong>{v.label}</strong> — {v.desc}</span>
                          {voiceId === v.id && (
                            <motion.span className="locu-dropdown__item-check" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 500, damping: 25 }}>
                              <i className="fa-solid fa-check" />
                            </motion.span>
                          )}
                        </li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>

              {/* Indicador de custo conforme a voz escolhida */}
              <div className="locu-cost-indicator" style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600 }}>
                {voiceId === "" ? (
                  <span style={{ color: "#16a34a", display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <i className="fa-solid fa-circle-check" /> Voz padrão — Grátis
                  </span>
                ) : (
                  <span style={{ color: "#a16207", display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <i className="fa-solid fa-coins" /> Voz de locutor — 5 créditos
                  </span>
                )}
              </div>

              {(selectedVoice || voiceId === "") && previewUrl && (
                <div className="locu-preview-row">
                  <button className="locu-play-btn" onClick={togglePlay} style={{ flex: 1 }}>
                    <div className="locu-play-btn__circle"><i className={`fa-solid ${tocando ? "fa-pause" : "fa-play"}`} /></div>
                    <span>{tocando ? "Pausar exemplo" : `Ouvir exemplo — ${selectedVoice ? selectedVoice.label : "Padrão"}`}</span>
                  </button>
                </div>
              )}

              <audio ref={audioRef} key={previewUrl} src={previewUrl} onPlay={() => setTocando(true)} onPause={() => setTocando(false)} onEnded={() => setTocando(false)} style={{ display: "none" }} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Introducao */}
        {voz !== "gravar" && (
          <div className="locu-section">
            <div className="locu-field-header">
              <i className="fa-solid fa-bullhorn locu-field-header__icon" />
              <label className="edit-label">Introducao</label>
            </div>
            <textarea className="locu-textarea locu-textarea-intro" rows={3} defaultValue={textoIntroInicial} placeholder="Texto que sera narrado na abertura do video..." />
          </div>
        )}

        {/* Fechamento */}
        {voz !== "gravar" && (
          <div className="locu-section">
            <div className="locu-field-header">
              <i className="fa-solid fa-flag-checkered locu-field-header__icon" />
              <label className="edit-label">Fechamento</label>
            </div>
            <textarea className="locu-textarea locu-textarea-outro" rows={3} value={textoOutro} onChange={(e) => setTextoOutro(e.target.value)} placeholder="Texto que sera narrado no encerramento (ex.: aproveite, ofertas validas ate...)" />
          </div>
        )}

        {/* Produtos (oculto no modo gravar) */}
        {produtos?.length > 0 && voz !== "gravar" && (
          <div className="locu-section">
            <div className="locu-field-header" style={{ marginBottom: 10 }}>
              <i className="fa-solid fa-box-open locu-field-header__icon" />
              <label className="edit-label">Produtos ({produtos.length})</label>
            </div>
            <div className="locu-products-list">
              <AnimatePresence>
                {produtos.map((p, index) => (
                  <motion.div key={p.id || index} className={`locu-product-item${gerando && progressIndex === index + 1 ? " locu-product-item--active" : ""}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.04, duration: 0.25 }}>
                    <div className="locu-product-item__head">
                      <span className="locu-product-item__badge">{index + 1}</span>
                      <span className="locu-product-item__name">{p.titulo}</span>
                      {gerando && progressIndex === index + 1 && <span className="prod-spinner-sm" />}
                      {gerando && progressIndex > index + 1 && <i className="fa-solid fa-check locu-product-item__done" />}
                    </div>
                    <textarea className="locu-textarea locu-textarea-produto" rows={2} defaultValue={p.texto_audio} placeholder={`Texto de narracao para ${p.titulo}...`} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        )}

        {/* Progress bar + Generate button */}
        <div className="locu-footer">
          {gerando && (
            <motion.div className="locu-progress" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
              <div className="locu-progress__bar">
                <motion.div className="locu-progress__fill" initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 0.3 }} />
              </div>
              <span className="locu-progress__text">{statusBotao}</span>
            </motion.div>
          )}

          <button
            className="locu-generate-btn"
            onClick={handleBotaoPrincipal}
            disabled={gerando || (voz === "locutor" && locutorBloqueado)}
            style={
              gravacaoStep === "recording"
                ? { background: "#ef4444", color: "#fff", borderColor: "#ef4444" }
                : gravacaoStep === "finished"
                ? { background: "#73207c", color: "#fff", borderColor: "#73207c" }
                : {}
            }
          >
            {gerando ? (
              <><span className="prod-spinner-sm" /> {statusBotao}</>
            ) : gravacaoStep === "recording" ? (
              <>
                <i className="fa-solid fa-circle" style={{ color: "#fff", fontSize: 10, animation: "pulse 1s infinite" }} />
                <span>Gravando • Parar gravação</span>
              </>
            ) : gravacaoStep === "finished" ? (
              <><i className="fa-solid fa-rotate-right" /> Gravar novamente</>
            ) : voz === "gravar" ? (
              <><i className="fa-solid fa-microphone" /> Gravar Voz</>
            ) : (
              <><i className="fa-solid fa-wand-magic-sparkles" /> Gerar Locucao</>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export default Locucao;
